import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import { dayjs } from '../../Components/Dayjs/dayjs-utils';
import { UserOnline } from '../../Types/WebsocketServer';
import { getDevices as getGnssDevices, startSweep as startGnssSweep } from '../../Utils/api/Gnss/api';
import { getEnvironmentVariable } from '../../Utils/environment';
import getCDNImagePath from '../../Utils/getCDNImagePath';
import Clock from './Clock';
import InfoscreenFullscreenView from './InfoscreenFullscreenView';
import InfoscreenLayoutView from './InfoscreenLayoutView';
import { acquireSpectrumWsConnection } from './SpectrumWsConnection';

import type { Dashboard, infoscreenLayoutView, ProfileListUser, SiteSelect } from '../../Types/AuthServer';
import type { WsDeviceInfo } from '../../Types/Gnss';

const STATUS_COLORS = {
    away: '#adadb8',
    busy: '#E72D50',
    present: '#8DBF30',
    recently: '#FCC041',
};

export interface InfoscreenProps {
    activeViewIndex?: number;
    availableUsers?: ProfileListUser[];
    data: Dashboard['socials'] | undefined;
    enableLive?: boolean;
    fullscreen?: boolean; // For preview mode
    ignoreActiveSchedule?: boolean;
    invalidUserIds?: string[];
    // Optional editing props
    isEditingMode?: boolean;

    onActiveViewIndexChange?: (index: number) => void;
    // Callback to inform parent about current view type for header/footer visibility
    onCurrentViewTypeChange?: (viewType: string | null) => void;
    onExitPreview?: () => void;
    onlineData: UserOnline[] | undefined;
    onRemoveUser?: (row: number, col: number) => void;
    onUserSelect?: (userId: string, row: number, col: number) => void;
    // Custom profile card renderer for editing mode
    renderProfileCard?: (props: {
        col: number;
        isEditingMode?: boolean;
        onlineUserData?: UserOnline;
        row: number;
        userData?: ProfileListUser;
        userId: string | null;
    }) => React.ReactNode;
    selectedViewId?: string; // Show only this view in editing mode
    showBackgroundForImages?: boolean; // UI-only toggle for showing background behind fullscreen images
    sites?: SiteSelect[];

    small?: boolean;

    users: ProfileListUser[] | undefined;
}

// Helper function to check if current time is within active hours and days
const isInfoscreenActive = (infoscreenLayout: Dashboard['socials'] | undefined): boolean => {
    if (!infoscreenLayout?.onlineTime) {
        return true;
    }

    try {
        // Get current time in the infoscreen's timezone
        const timezone = infoscreenLayout.tz || 'UTC';
        const nowInTimezone = dayjs().tz(timezone);

        // Get current day where 1 = Monday, 2 = Tuesday, ... 7 = Sunday
        // dayjs day() returns 0 for Sunday, so we need to adjust
        const dayOfWeek = nowInTimezone.day();
        const currentDay = dayOfWeek === 0 ? 7 : dayOfWeek; // Convert to 1 = Monday, 7 = Sunday
        const currentTime = nowInTimezone.hour() * 60 + nowInTimezone.minute(); // Convert to minutes since midnight

        // Check if current day is in active days
        const activeDays = infoscreenLayout.onlineTime.showWeekDay || [];
        if (activeDays.length > 0 && !activeDays.includes(currentDay)) {
            return false;
        }

        // Check if current time is within active hours
        const startTime = infoscreenLayout.onlineTime.start;
        const endTime = infoscreenLayout.onlineTime.end;

        if (startTime && endTime) {
            // Parse time string - handle both duration format and time format
            const parseTimeToMinutes = (timeStr: string): number => {
                // If it's an ISO 8601 duration format (PT4H, PT4H30M, etc.)
                if (timeStr.startsWith('PT')) {
                    const durationRegex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
                    const match = durationRegex.exec(timeStr);
                    if (!match) {
                        return 0;
                    }

                    const hours = Number.parseInt(match[1] || '0', 10);
                    const minutes = Number.parseInt(match[2] || '0', 10);
                    const seconds = Number.parseInt(match[3] || '0', 10);

                    // Convert to total minutes (ignoring seconds for now as we work with minute precision)
                    const totalMinutes = hours * 60 + minutes + Math.floor(seconds / 60);

                    // Ensure we don't exceed 24 hours (1440 minutes)
                    return Math.min(totalMinutes, 1440);
                }

                // If it's a time format (HH:MM or HH:MM:SS)
                if (timeStr.includes(':')) {
                    const timeParts = timeStr.split(':');
                    const hours = Number.parseInt(timeParts[0] || '0', 10);
                    const minutes = Number.parseInt(timeParts[1] || '0', 10);
                    return hours * 60 + minutes;
                }

                // If it's just a number, treat as minutes from midnight
                const parsed = Number.parseInt(timeStr, 10);
                return isNaN(parsed) ? 0 : parsed;
            };

            const startMinutes = parseTimeToMinutes(startTime);
            const endMinutes = parseTimeToMinutes(endTime);

            // Handle overnight intervals (e.g., 22:00 to 06:00)
            if (startMinutes > endMinutes) {
                // Overnight interval: active if current time is after start OR before end
                const isActive = currentTime >= startMinutes || currentTime <= endMinutes;
                return isActive;
            } else {
                // Normal interval: active if current time is between start and end
                const isActive = currentTime >= startMinutes && currentTime <= endMinutes;
                return isActive;
            }
        }

        return true;
    } catch (error) {
        console.error('Error checking infoscreen activity:', error);
        // Default to active if there's an error with timezone handling
        return true;
    }
};

export default function Infoscreen({
    activeViewIndex,
    availableUsers,
    data,
    enableLive = true,
    fullscreen = false,
    ignoreActiveSchedule = false,
    invalidUserIds,
    isEditingMode = false,
    onActiveViewIndexChange,
    onCurrentViewTypeChange,
    onExitPreview,
    onlineData,
    onRemoveUser,
    onUserSelect,
    renderProfileCard,
    selectedViewId,
    showBackgroundForImages = false,
    sites,
    small,
    users,
}: Readonly<InfoscreenProps>) {
    const basePath = getEnvironmentVariable('EXPO_PUBLIC_IMAGE_SERVE_HOST') ?? '';
    const infoscreenLayout = data;
    const isActive = ignoreActiveSchedule || isInfoscreenActive(infoscreenLayout);
    const sweepInitRef = useRef(false);
    const navigationScrollContainerRef = useRef<HTMLDivElement | null>(null);
    const navigationButtonRefs = useRef<Record<number, HTMLButtonElement | null>>({});

    // Carousel state for switching between views
    const [uncontrolledViewIndex, setUncontrolledViewIndex] = useState(0);

    // Get views to display
    const allViews = infoscreenLayout?.layoutViews || [];
    const viewsToDisplay = (() => {
        if (isEditingMode && selectedViewId) {
            return allViews.filter((view) => view.id.toString() === selectedViewId);
        }
        return allViews;
    })();

    const isControlledViewIndex = activeViewIndex != null;
    const boundedControlledViewIndex =
        activeViewIndex == null || viewsToDisplay.length === 0
            ? 0
            : Math.max(0, Math.min(viewsToDisplay.length - 1, activeViewIndex));
    const currentViewIndex = isControlledViewIndex ? boundedControlledViewIndex : uncontrolledViewIndex;

    const setViewIndex = useCallback(
        (nextIndexOrUpdater: number | ((previousIndex: number) => number)) => {
            const previousIndex = isControlledViewIndex ? boundedControlledViewIndex : uncontrolledViewIndex;
            const rawNextIndex =
                typeof nextIndexOrUpdater === 'function' ? nextIndexOrUpdater(previousIndex) : nextIndexOrUpdater;

            if (viewsToDisplay.length === 0) {
                if (!isControlledViewIndex && uncontrolledViewIndex !== 0) {
                    setUncontrolledViewIndex(0);
                }

                if (isControlledViewIndex && boundedControlledViewIndex !== 0) {
                    onActiveViewIndexChange?.(0);
                }

                return;
            }

            const boundedNextIndex = Math.max(0, Math.min(viewsToDisplay.length - 1, rawNextIndex));

            if (boundedNextIndex === previousIndex) {
                return;
            }

            if (!isControlledViewIndex) {
                setUncontrolledViewIndex(boundedNextIndex);
            }

            onActiveViewIndexChange?.(boundedNextIndex);
        },
        [
            boundedControlledViewIndex,
            isControlledViewIndex,
            onActiveViewIndexChange,
            uncontrolledViewIndex,
            viewsToDisplay.length,
        ],
    );

    const getNextViewIndex = useCallback(
        (index: number): number => {
            const viewCount = viewsToDisplay.length;
            if (viewCount === 0) {
                return 0;
            }
            return (index + 1) % viewCount;
        },
        [viewsToDisplay.length],
    );

    const getPreviousViewIndex = useCallback(
        (index: number): number => {
            const viewCount = viewsToDisplay.length;
            if (viewCount === 0) {
                return 0;
            }
            return (index - 1 + viewCount) % viewCount;
        },
        [viewsToDisplay.length],
    );

    const goToNextView = useCallback(() => {
        setViewIndex((previousIndex) => getNextViewIndex(previousIndex));
    }, [getNextViewIndex, setViewIndex]);

    const goToPreviousView = useCallback(() => {
        setViewIndex((previousIndex) => getPreviousViewIndex(previousIndex));
    }, [getPreviousViewIndex, setViewIndex]);

    useEffect(() => {
        if (viewsToDisplay.length === 0) {
            if (currentViewIndex !== 0) {
                setViewIndex(0);
            }
            return;
        }

        if (currentViewIndex > viewsToDisplay.length - 1) {
            setViewIndex(viewsToDisplay.length - 1);
        }
    }, [currentViewIndex, setViewIndex, viewsToDisplay.length]);

    const getSpectrumDeviceNumber = useCallback((view: infoscreenLayoutView, fallbackIndex: number): string => {
        const sensorSerial = view.spectrumConfig?.sensorSerial || '';
        const match = /^MLS0040000(\d+)123-SPEC$/.exec(sensorSerial);
        if (match?.[1]) {
            const parsed = Number.parseInt(match[1], 10);
            if (!Number.isNaN(parsed)) {
                return parsed.toString();
            }
        }
        return (fallbackIndex + 1).toString();
    }, []);

    const spectrumMapTargets = viewsToDisplay
        .map((view, index) => {
            if (view.type !== 'spectrum' || !view.spectrumConfig?.sensorSerial) {
                return null;
            }

            return {
                deviceNumber: getSpectrumDeviceNumber(view, index),
                sensorSerial: view.spectrumConfig.sensorSerial,
                targetViewIndex: index,
            };
        })
        .filter((value): value is { deviceNumber: string; sensorSerial: string; targetViewIndex: number } =>
            Boolean(value),
        );

    // Filter views for InfoscreenLayoutView component (views with header/footer layout)
    const layoutViews = viewsToDisplay.filter((view) => view.type === 'people');
    const spectrumDeviceIds = Array.from(
        new Set(
            viewsToDisplay
                .filter((view) => view.type === 'spectrum')
                .map((view) => view.spectrumConfig?.sensorSerial || '')
                .filter((value) => value !== ''),
        ),
    );
    const hasSpectrumViews = spectrumDeviceIds.length > 0;

    // UI sizes for header/footer elements
    const UI_SIZES = {
        clockFontSize: small ? '12px !important' : 'clamp(12px, 0.95vw, 22px) !important',
        legendLabelSize: small ? '10px !important' : 'clamp(10px, 0.85vw, 16px) !important',
        legendSize: small ? 10 : 'clamp(8px, 0.6vw, 14px)',
        titleFontSize: small ? '12px !important' : 'clamp(14px, 1.1vw, 26px) !important',
    };

    const renderStatusLegendItem = (status: 'present' | 'recently' | 'away' | 'busy', label: string) => (
        <>
            <Chip
                label=" "
                sx={{
                    backgroundColor: STATUS_COLORS[status],
                    borderRadius: '50%',
                    height: UI_SIZES.legendSize,
                    width: UI_SIZES.legendSize,
                }}
            />
            <Typography sx={{ color: infoscreenLayout?.textColor, fontSize: UI_SIZES.legendLabelSize }}>
                {label}
            </Typography>
        </>
    );

    const manualSlideOnly = infoscreenLayout?.manualSlideOnly ?? false;
    const enableArrowNavigation = infoscreenLayout?.enableArrowNavigation ?? false;
    const showSlideNavigationButtons = infoscreenLayout?.showSlideNavigationButtons ?? false;
    const isPreviewMode = isEditingMode && fullscreen;

    // Determine if we should enable carousel
    const shouldEnableCarousel =
        !isEditingMode &&
        !manualSlideOnly &&
        infoscreenLayout?.carouselInterval &&
        infoscreenLayout.carouselInterval > 0 &&
        viewsToDisplay.length > 1;
    const shouldUseCarouselTransitionLayout = !viewsToDisplay.some(
        (view) => view.type === 'spectrum' || view.type === 'spectrumMap',
    );

    const canNavigateWithInput = !isEditingMode && viewsToDisplay.length > 1;
    const shouldShowNavigationButtons =
        (isPreviewMode || !isEditingMode) && showSlideNavigationButtons && viewsToDisplay.length > 1;

    const centerNavigationButton = useCallback((index: number) => {
        const container = navigationScrollContainerRef.current;
        const buttonElement = navigationButtonRefs.current[index];
        if (!container || !buttonElement) {
            return;
        }

        const rawTargetScroll = buttonElement.offsetLeft + buttonElement.clientWidth / 2 - container.clientWidth / 2;
        const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
        const targetScroll = Math.max(0, Math.min(maxScroll, rawTargetScroll));

        container.scrollTo({
            behavior: 'smooth',
            left: targetScroll,
        });
    }, []);

    const getViewNavigationLabel = useCallback(
        (view: infoscreenLayoutView, index: number) => {
            const navigationLabelValue = (view as { navigationLabel?: string }).navigationLabel;
            const customNavigationLabel = typeof navigationLabelValue === 'string' ? navigationLabelValue.trim() : '';

            if (customNavigationLabel) {
                return customNavigationLabel;
            }

            if (view.type === 'spectrum') {
                return `Device ${getSpectrumDeviceNumber(view, index)}`;
            }

            if (view.type === 'spectrumMap') {
                return 'Device Map';
            }

            if (view.type === 'roomCalendar') {
                const roomNameValue = (view.roomCalendarConfig as { roomName?: string } | undefined)?.roomName;
                return typeof roomNameValue === 'string' && roomNameValue.trim() !== ''
                    ? roomNameValue.trim()
                    : `View ${(index + 1).toString()}`;
            }

            return `View ${(index + 1).toString()}`;
        },
        [getSpectrumDeviceNumber],
    );

    const navigateToView = useCallback(
        (index: number) => {
            setViewIndex(index);
            requestAnimationFrame(() => {
                centerNavigationButton(index);
            });
        },
        [centerNavigationButton, setViewIndex],
    );

    // Handle carousel auto-advance
    useEffect(() => {
        if (!shouldEnableCarousel) {
            return;
        }

        const interval = setInterval(
            () => {
                goToNextView();
            },
            (infoscreenLayout.carouselInterval || 30) * 1000,
        );

        return () => clearInterval(interval);
    }, [currentViewIndex, goToNextView, infoscreenLayout?.carouselInterval, shouldEnableCarousel]);

    useEffect(() => {
        if (!canNavigateWithInput) {
            return;
        }

        // Rail mode wins when legacy data has both flags true — no migration/write-back needed.
        const effectiveRailMode = showSlideNavigationButtons;
        const effectiveArrowMode = enableArrowNavigation && !showSlideNavigationButtons;

        const handleKeyDown = (event: KeyboardEvent) => {
            const activeElement = document.activeElement;
            const navigationContainerElement = navigationScrollContainerRef.current;
            if (
                navigationContainerElement &&
                activeElement instanceof HTMLElement &&
                navigationContainerElement.contains(activeElement)
            ) {
                return;
            }

            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
                return;
            }

            if (effectiveArrowMode) {
                event.preventDefault();
                if (event.key === 'ArrowLeft') {
                    goToPreviousView();
                } else {
                    goToNextView();
                }
                return;
            }

            if (effectiveRailMode) {
                // Cold start: a TV remote can't Tab to the rail, so the first arrow press
                // focuses the current slide's nav button instead of doing nothing.
                event.preventDefault();
                const currentButton = navigationButtonRefs.current[currentViewIndex];
                if (currentButton) {
                    currentButton.focus();
                    centerNavigationButton(currentViewIndex);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        canNavigateWithInput,
        centerNavigationButton,
        currentViewIndex,
        enableArrowNavigation,
        goToNextView,
        goToPreviousView,
        showSlideNavigationButtons,
    ]);

    useEffect(() => {
        if (!enableLive || !hasSpectrumViews) {
            sweepInitRef.current = false;
            return;
        }

        if (sweepInitRef.current) {
            return;
        }

        sweepInitRef.current = true;

        const runSweepStart = async () => {
            try {
                const res = await getGnssDevices();
                if (!res.ok || !res.data) {
                    return;
                }

                const devices = Array.isArray(res.data) ? res.data : Object.values(res.data);
                await Promise.all(
                    devices.map(async (device: WsDeviceInfo) => {
                        if (!spectrumDeviceIds.includes(device.id)) {
                            return;
                        }
                        const isSweeping = device.enabled === 1;
                        if (!isSweeping) {
                            await startGnssSweep(device.id);
                        }
                    }),
                );
            } catch (error) {
                console.error('Failed to start sweep for spectrum devices:', error);
            }
        };

        void runSweepStart();
    }, [enableLive, hasSpectrumViews, spectrumDeviceIds]);

    useEffect(() => {
        if (!hasSpectrumViews) {
            return;
        }

        const wsHost = getEnvironmentVariable('EXPO_PUBLIC_SDR_WS_HOST');
        if (!wsHost) {
            console.error('Missing EXPO_PUBLIC_SDR_WS_HOST for spectrum view');
            return;
        }

        const noopStateChange = () => undefined;
        const { release } = acquireSpectrumWsConnection(wsHost, noopStateChange);

        return () => {
            release();
        };
    }, [hasSpectrumViews]);

    // Report current view type to parent
    const reportCurrentViewType = useCallback(() => {
        if (!onCurrentViewTypeChange || viewsToDisplay.length === 0) {
            return;
        }

        const currentView = viewsToDisplay[currentViewIndex];
        if (!currentView) {
            return;
        }

        const viewType = currentView.type === 'people' ? 'normal' : 'fullscreen';
        onCurrentViewTypeChange(viewType);
    }, [viewsToDisplay, currentViewIndex, onCurrentViewTypeChange]);

    // Report view type when index changes
    useEffect(() => {
        reportCurrentViewType();
    }, [currentViewIndex, reportCurrentViewType]);

    useEffect(() => {
        if (!shouldShowNavigationButtons) {
            return;
        }

        const animationFrameId = requestAnimationFrame(() => {
            centerNavigationButton(currentViewIndex);
        });

        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, [centerNavigationButton, currentViewIndex, shouldShowNavigationButtons]);

    // Get current view
    const currentView = viewsToDisplay[currentViewIndex];

    // Determine which component to render based on view type
    const isFullscreenView = currentView ? currentView.type !== 'people' : false;

    // In editing mode, always render the appropriate component directly without transitions
    if (isEditingMode && isFullscreenView && currentView) {
        return createFullscreenContainer({
            basePath,
            children: !isActive ? (
                <SleepingScreen small={small} textColor={infoscreenLayout?.textColor} />
            ) : (
                <InfoscreenFullscreenView
                    enableLive={enableLive}
                    infoscreenLayout={infoscreenLayout}
                    isActiveView
                    onSpectrumMapNavigate={navigateToView}
                    showBackgroundForImages={showBackgroundForImages}
                    sites={sites ?? []}
                    spectrumMapTargets={spectrumMapTargets}
                    view={currentView}
                />
            ),
            fullscreen,
            infoscreenLayout,
            onExitPreview,
            showBackgroundForImages,
        });
    }

    const renderNavigationButtons = () => {
        if (!shouldShowNavigationButtons) {
            return null;
        }

        return (
            <Box
                ref={navigationScrollContainerRef}
                sx={{
                    backgroundColor: 'rgba(0, 0, 0)',
                    borderTop: '1px solid rgba(255,255,255,0.15)',
                    boxSizing: 'border-box',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    px: { sm: 1, xs: 0.5 },
                    py: 0.75,
                    scrollbarWidth: 'thin',
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexWrap: 'nowrap',
                        gap: 0.4,
                        justifyContent: 'center',
                        minWidth: 'max-content',
                        pr: 0.5,
                    }}
                >
                    {viewsToDisplay.map((view, index) => {
                        const isCurrentView = index === currentViewIndex;
                        const navigationButtonCount = viewsToDisplay.length;
                        const label = getViewNavigationLabel(view, index);

                        return (
                            <Button
                                key={`navigation-${view.id.toString()}`}
                                onClick={() => {
                                    navigateToView(index);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'ArrowRight') {
                                        event.preventDefault();
                                        const nextIndex = (index + 1) % navigationButtonCount;
                                        const nextButton = navigationButtonRefs.current[nextIndex];
                                        if (nextButton) {
                                            nextButton.focus();
                                        }
                                        centerNavigationButton(nextIndex);
                                        return;
                                    }

                                    if (event.key === 'ArrowLeft') {
                                        event.preventDefault();
                                        const previousIndex =
                                            (index - 1 + navigationButtonCount) % navigationButtonCount;
                                        const previousButton = navigationButtonRefs.current[previousIndex];
                                        if (previousButton) {
                                            previousButton.focus();
                                        }
                                        centerNavigationButton(previousIndex);
                                        return;
                                    }

                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        navigateToView(index);
                                    }
                                }}
                                ref={(element: HTMLButtonElement | null) => {
                                    navigationButtonRefs.current[index] = element;
                                }}
                                size="small"
                                sx={{
                                    borderColor: 'rgba(255, 255, 255, 0.6)',
                                    color: isCurrentView ? undefined : '#ffffff',
                                    flexShrink: 0,
                                    fontSize: small ? 10 : 'clamp(9px, 0.7vw, 12px)',
                                    lineHeight: 1.2,
                                    minWidth: 0,
                                    px: { sm: 0.85, xs: 0.6 },
                                    py: 0.2,
                                    whiteSpace: 'nowrap',
                                }}
                                variant={isCurrentView ? 'contained' : 'outlined'}
                            >
                                {label}
                            </Button>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    const renderLogo = (imagePath?: string) =>
        imagePath && (
            <Box
                alt="Logo"
                component="img"
                src={`${basePath}/${imagePath}.webp`}
                sx={{
                    height: 'auto',
                    maxHeight: '75%',
                    maxWidth: '100%',
                    objectFit: 'contain',
                    px: '5%',
                    width: 'clamp(56px, 6.5vw, 140px)',
                }}
            />
        );

    // Function to render normal view with header and footer
    const renderNormalViewWithHeaderFooter = () => {
        if (!infoscreenLayout) {
            return null;
        }

        return (
            <Stack
                sx={{
                    flex: 1,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        backgroundColor: infoscreenLayout.backgroundColor ?? '#000000',
                        backgroundImage: infoscreenLayout.backgroundImage
                            ? `url(${basePath}/${infoscreenLayout.backgroundImage}.webp)`
                            : '',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: infoscreenLayout.backgroundSize || 'cover',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    }}
                >
                    {/* Header - always shown in normal wrapper */}
                    <Stack
                        direction="row"
                        sx={{
                            alignItems: 'center',
                            height: '10%',
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* First part - Logo */}
                        <Stack
                            sx={{
                                flex: 1,
                                height: '100%',
                                justifyContent: 'center',
                                maxWidth: 1 / 3,
                            }}
                        >
                            {!small && renderLogo(infoscreenLayout.image1)}
                        </Stack>

                        {/* Second part - Title or second logo */}
                        <Stack
                            sx={{
                                alignItems: 'center',
                                flex: 1,
                                height: '100%',
                                justifyContent: 'center',
                                maxWidth: 1 / 3,
                            }}
                        >
                            {!small && infoscreenLayout.hasSecondLogo ? (
                                renderLogo(infoscreenLayout.image2)
                            ) : (
                                <Typography
                                    align="center"
                                    sx={{
                                        color: infoscreenLayout.titleColor,
                                        display: '-webkit-box',
                                        fontSize: UI_SIZES.titleFontSize,
                                        lineHeight: 1.15,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 2,
                                    }}
                                >
                                    {infoscreenLayout.title}
                                </Typography>
                            )}
                        </Stack>

                        {/* Third part - Clock */}
                        <Stack
                            sx={{
                                alignItems: 'flex-end',
                                display: 'flex',
                                flex: 1,
                                height: '100%',
                                justifyContent: 'center',
                                maxWidth: 1 / 3,
                            }}
                        >
                            <Box
                                sx={{
                                    px: 4,
                                }}
                            >
                                {!small && (
                                    <Clock
                                        clockColor={infoscreenLayout.clockColor}
                                        clockSize={UI_SIZES.clockFontSize}
                                        tz={infoscreenLayout.tz}
                                    />
                                )}
                            </Box>
                        </Stack>
                    </Stack>

                    {/* Main content area - fixed 84% height */}
                    <Stack
                        component="main"
                        sx={{
                            alignItems: 'center',
                            alignSelf: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '84%',
                            maxWidth: 3000,
                            width: '100%',
                        }}
                    >
                        {!isActive ? (
                            // Sleeping cat screen when infoscreen is outside active hours/days
                            <Stack
                                sx={{
                                    alignItems: 'center',
                                    color: infoscreenLayout.textColor || '#ffffff',
                                    display: 'flex',
                                    flex: 1,
                                    gap: 3,
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: small ? '24px' : '3rem',
                                        fontWeight: 'bold',
                                        mb: 2,
                                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                                    }}
                                >
                                    😴
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: small ? '16px' : '2rem',
                                        fontWeight: 'medium',
                                        mb: 1,
                                        textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                                    }}
                                >
                                    Infoscreen is sleeping
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: small ? '12px' : '1.2rem',
                                        opacity: 0.8,
                                        textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                                    }}
                                >
                                    Come back during active hours
                                </Typography>
                                {infoscreenLayout.tz ? (
                                    <Typography
                                        sx={{
                                            fontSize: small ? '10px' : '1rem',
                                            mt: 1,
                                            opacity: 0.6,
                                            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                                        }}
                                    >
                                        Timezone: {infoscreenLayout.tz}
                                    </Typography>
                                ) : null}
                            </Stack>
                        ) : currentView ? (
                            <InfoscreenLayoutView
                                availableUsers={availableUsers}
                                carouselInterval={infoscreenLayout.carouselInterval}
                                infoscreenLayout={infoscreenLayout}
                                invalidUserIds={invalidUserIds}
                                isEditingMode={isEditingMode}
                                layoutViews={[currentView]}
                                onlineData={onlineData}
                                onRemoveUser={onRemoveUser}
                                onUserSelect={onUserSelect}
                                renderProfileCard={renderProfileCard}
                                showBackgroundForImages={showBackgroundForImages}
                                sites={sites ?? []}
                                small={small}
                                users={users}
                            />
                        ) : null}
                    </Stack>

                    {/* Footer - always shown in normal wrapper */}
                    <Box
                        component="footer"
                        sx={{
                            alignItems: 'center',
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            display: 'flex',
                            height: '6%',
                        }}
                    >
                        <Stack
                            direction="row"
                            sx={{
                                height: '100%',
                                justifyContent: 'space-between',
                                width: '100%',
                            }}
                        >
                            {/* Left section - Full status legend */}
                            <Stack
                                sx={{
                                    alignItems: 'flex-start',
                                    display: 'flex',
                                    flex: 1,
                                    height: '100%',
                                    justifyContent: 'center',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing="4%"
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        flex: 1,
                                        px: '5%',
                                    }}
                                >
                                    {renderStatusLegendItem('present', 'Present')}
                                    {renderStatusLegendItem('recently', 'Recently')}
                                    {renderStatusLegendItem('away', 'Away')}
                                    {renderStatusLegendItem('busy', 'Busy')}
                                </Stack>
                            </Stack>

                            {/* Middle section - Minimal status legend - hidden for now */}
                            <Stack
                                sx={{
                                    alignItems: 'center',
                                    flex: 1,
                                    height: '100%',
                                    justifyContent: 'center',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing="3%"
                                    sx={{
                                        alignItems: 'center',
                                        display: 'none',
                                        pt: '4%',
                                        px: '5%',
                                    }}
                                >
                                    {renderStatusLegendItem('present', 'Present')}
                                    {renderStatusLegendItem('recently', 'Recently')}
                                </Stack>
                            </Stack>

                            {/* Right section - Logo */}
                            <Stack
                                sx={{
                                    alignItems: 'flex-end',
                                    display: 'flex',
                                    flex: 1,
                                    height: '100%',
                                    justifyContent: 'center',
                                    px: '1%',
                                }}
                            >
                                {!small && (
                                    <Box
                                        component="img"
                                        src={getCDNImagePath('renki/poweredByRenki_white.png')}
                                        sx={{
                                            aspectRatio: '457/187',
                                            height: 'auto',
                                            maxHeight: '100%',
                                            objectFit: 'contain',
                                        }}
                                    />
                                )}
                            </Stack>
                        </Stack>
                    </Box>
                </Box>
            </Stack>
        );
    };

    // For TV mode with carousel, or editing mode with normal views, render with potential transitions
    if (!isEditingMode && shouldEnableCarousel && shouldUseCarouselTransitionLayout && viewsToDisplay.length > 1) {
        // TV mode with carousel - render both component types with fade transitions
        return (
            <Box
                sx={{
                    backgroundColor: infoscreenLayout.backgroundColor ?? '#000000',
                    backgroundImage:
                        infoscreenLayout.backgroundImage && showBackgroundForImages
                            ? `url(${basePath}/${infoscreenLayout.backgroundImage}.webp)`
                            : '',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: infoscreenLayout.backgroundSize || 'cover',
                    display: 'flex',
                    flexDirection: 'column',
                    height: fullscreen ? '100vh' : '100%',
                    left: fullscreen ? 0 : 'auto',
                    position: fullscreen ? 'fixed' : 'relative',
                    top: fullscreen ? 0 : 'auto',
                    width: fullscreen ? '100vw' : '100%',
                    zIndex: fullscreen ? 1300 : 'auto',
                }}
            >
                <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
                    {/* Render all views with fade transitions */}
                    {viewsToDisplay.map((view, index) => {
                        const isCurrentView = index === currentViewIndex;
                        const isFullscreen = view.type !== 'people';

                        if (isFullscreen) {
                            // Fullscreen view without header/footer
                            return (
                                <Box
                                    key={`fullscreen-${view.id.toString()}`}
                                    sx={{
                                        height: '100%',
                                        left: 0,
                                        opacity: isCurrentView ? 1 : 0,
                                        pointerEvents: isCurrentView ? 'auto' : 'none',
                                        position: 'absolute',
                                        top: 0,
                                        transition: 'opacity 0.5s ease-in-out',
                                        width: '100%',
                                        zIndex: isCurrentView ? 1 : 0,
                                    }}
                                >
                                    {!isActive ? (
                                        // Sleeping screen for fullscreen mode
                                        <Stack
                                            sx={{
                                                alignItems: 'center',
                                                color: infoscreenLayout.textColor || '#ffffff',
                                                display: 'flex',
                                                gap: 3,
                                                height: '100%',
                                                justifyContent: 'center',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: small ? '24px' : '3rem',
                                                    fontWeight: 'bold',
                                                    mb: 2,
                                                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                                                }}
                                            >
                                                😴
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: small ? '16px' : '2rem',
                                                    fontWeight: 'medium',
                                                    mb: 1,
                                                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                                                }}
                                            >
                                                Infoscreen is sleeping
                                            </Typography>
                                        </Stack>
                                    ) : (
                                        <InfoscreenFullscreenView
                                            enableLive={enableLive}
                                            infoscreenLayout={infoscreenLayout}
                                            isActiveView={isCurrentView}
                                            onSpectrumMapNavigate={navigateToView}
                                            showBackgroundForImages={showBackgroundForImages}
                                            sites={sites ?? []}
                                            spectrumMapTargets={spectrumMapTargets}
                                            view={view}
                                        />
                                    )}
                                </Box>
                            );
                        }

                        // Normal view with header/footer
                        return (
                            <Box
                                key={`normal-${view.id.toString()}`}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%',
                                    left: 0,
                                    opacity: isCurrentView ? 1 : 0,
                                    pointerEvents: isCurrentView ? 'auto' : 'none',
                                    position: 'absolute',
                                    top: 0,
                                    transition: 'opacity 0.5s ease-in-out',
                                    width: '100%',
                                    zIndex: isCurrentView ? 1 : 0,
                                }}
                            >
                                {renderNormalViewWithHeaderFooter()}
                            </Box>
                        );
                    })}
                </Box>
                {renderNavigationButtons()}
            </Box>
        );
    }

    // Single view mode or editing mode with normal views - render directly without transitions

    // Fullscreen image/graph views - no header/footer
    if (isFullscreenView && currentView) {
        return createFullscreenContainer({
            basePath,
            children: (
                <Stack sx={{ height: '100%' }}>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        {!isActive ? (
                            <SleepingScreen small={small} textColor={infoscreenLayout?.textColor} />
                        ) : (
                            <InfoscreenFullscreenView
                                enableLive={enableLive}
                                infoscreenLayout={infoscreenLayout}
                                isActiveView
                                onSpectrumMapNavigate={navigateToView}
                                showBackgroundForImages={showBackgroundForImages}
                                sites={sites ?? []}
                                spectrumMapTargets={spectrumMapTargets}
                                view={currentView}
                            />
                        )}
                    </Box>
                    {renderNavigationButtons()}
                </Stack>
            ),
            fullscreen,
            infoscreenLayout,
            onExitPreview,
            showBackgroundForImages,
        });
    }

    // Normal wrapper with header/footer for people/grid/fullscreen widget views
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: fullscreen ? '100vh' : '100%',
                left: fullscreen ? 0 : 'auto',
                position: fullscreen ? 'fixed' : 'relative',
                top: fullscreen ? 0 : 'auto',
                width: fullscreen ? '100vw' : '100%',
                zIndex: fullscreen ? 1300 : 'auto',
            }}
        >
            {fullscreen && onExitPreview ? (
                <IconButton
                    onClick={onExitPreview}
                    sx={{
                        ':hover': {
                            backgroundColor: 'bgBlue.main',
                        },
                        backgroundColor: 'primary.main',
                        color: 'white',
                        opacity: 0.9,
                        position: 'absolute',
                        right: 2.5,
                        top: 2.5,
                        zIndex: 1500,
                    }}
                >
                    <FullscreenExitIcon />
                </IconButton>
            ) : null}
            <Stack
                sx={{
                    flex: 1,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        backgroundColor: infoscreenLayout?.backgroundColor ?? '#000000',
                        backgroundImage: infoscreenLayout?.backgroundImage
                            ? `url(${basePath}/${infoscreenLayout.backgroundImage}.webp)`
                            : '',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: infoscreenLayout?.backgroundSize || 'cover',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    }}
                >
                    {/* Header - always shown in normal wrapper */}
                    <Stack
                        direction="row"
                        sx={{
                            alignItems: 'center',
                            height: '10%',
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* First part - Logo */}
                        <Stack
                            sx={{
                                flex: 1,
                                height: '100%',
                                justifyContent: 'center',
                                maxWidth: 1 / 3,
                            }}
                        >
                            {!small && renderLogo(infoscreenLayout?.image1)}
                        </Stack>

                        {/* Second part - Title or second logo */}
                        <Stack
                            sx={{
                                alignItems: 'center',
                                flex: 1,
                                height: '100%',
                                justifyContent: 'center',
                                maxWidth: 1 / 3,
                            }}
                        >
                            {!small && infoscreenLayout?.hasSecondLogo ? (
                                renderLogo(infoscreenLayout.image2)
                            ) : (
                                <Typography
                                    align="center"
                                    sx={{
                                        color: infoscreenLayout?.titleColor,
                                        display: '-webkit-box',
                                        fontSize: UI_SIZES.titleFontSize,
                                        lineHeight: 1.15,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 2,
                                    }}
                                >
                                    {infoscreenLayout?.title}
                                </Typography>
                            )}
                        </Stack>

                        {/* Third part - Clock */}
                        <Stack
                            sx={{
                                alignItems: 'flex-end',
                                display: 'flex',
                                flex: 1,
                                height: '100%',
                                justifyContent: 'center',
                                maxWidth: 1 / 3,
                            }}
                        >
                            <Box
                                sx={{
                                    px: 4,
                                }}
                            >
                                {!small && (
                                    <Clock
                                        clockColor={infoscreenLayout?.clockColor}
                                        clockSize={UI_SIZES.clockFontSize}
                                        tz={infoscreenLayout?.tz}
                                    />
                                )}
                            </Box>
                        </Stack>
                    </Stack>

                    {/* Main content area - fixed 84% height */}
                    <Stack
                        component="main"
                        sx={{
                            alignItems: 'center',
                            alignSelf: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '84%',
                            maxWidth: 3000,
                            width: '100%',
                        }}
                    >
                        {!isActive ? (
                            <SleepingScreen
                                showExtraText
                                small={small}
                                textColor={infoscreenLayout?.textColor}
                                timezone={infoscreenLayout?.tz}
                            />
                        ) : infoscreenLayout ? (
                            <InfoscreenLayoutView
                                availableUsers={availableUsers}
                                carouselInterval={infoscreenLayout.carouselInterval}
                                infoscreenLayout={infoscreenLayout}
                                invalidUserIds={invalidUserIds}
                                isEditingMode={isEditingMode}
                                layoutViews={layoutViews}
                                onlineData={onlineData}
                                onRemoveUser={onRemoveUser}
                                onUserSelect={onUserSelect}
                                renderProfileCard={renderProfileCard}
                                showBackgroundForImages={showBackgroundForImages}
                                sites={sites ?? []}
                                small={small}
                                users={users}
                            />
                        ) : (
                            <Stack
                                sx={{
                                    alignItems: 'center',
                                    color: '#ffffff',
                                    display: 'flex',
                                    flex: 1,
                                    justifyContent: 'center',
                                }}
                            >
                                Empty infoscreen
                            </Stack>
                        )}
                    </Stack>

                    {/* Footer - always shown in normal wrapper */}
                    <Box
                        component="footer"
                        sx={{
                            alignItems: 'center',
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            display: 'flex',
                            height: '6%',
                        }}
                    >
                        <Stack
                            direction="row"
                            sx={{
                                height: '100%',
                                justifyContent: 'space-between',
                                width: '100%',
                            }}
                        >
                            {/* Left section - Full status legend */}
                            <Stack
                                sx={{
                                    alignItems: 'flex-start',
                                    display: 'flex',
                                    flex: 1,
                                    height: '100%',
                                    justifyContent: 'center',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing="4%"
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        flex: 1,
                                        px: '5%',
                                    }}
                                >
                                    {renderStatusLegendItem('present', 'Present')}
                                    {renderStatusLegendItem('recently', 'Recently')}
                                    {renderStatusLegendItem('away', 'Away')}
                                    {renderStatusLegendItem('busy', 'Busy')}
                                </Stack>
                            </Stack>

                            {/* Middle section - Minimal status legend - hidden for now */}
                            <Stack
                                sx={{
                                    alignItems: 'center',
                                    flex: 1,
                                    height: '100%',
                                    justifyContent: 'center',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing="3%"
                                    sx={{
                                        alignItems: 'center',
                                        display: 'none',
                                        pt: '4%',
                                        px: '5%',
                                    }}
                                >
                                    {renderStatusLegendItem('present', 'Present')}
                                    {renderStatusLegendItem('recently', 'Recently')}
                                </Stack>
                            </Stack>

                            {/* Right section - Logo */}
                            <Stack
                                sx={{
                                    alignItems: 'flex-end',
                                    display: 'flex',
                                    flex: 1,
                                    height: '100%',
                                    justifyContent: 'center',
                                    px: '1%',
                                }}
                            >
                                {!small && (
                                    <Box
                                        component="img"
                                        src={getCDNImagePath('renki/poweredByRenki_white.png')}
                                        sx={{
                                            aspectRatio: '457/187',
                                            height: 'auto',
                                            maxHeight: '100%',
                                            objectFit: 'contain',
                                        }}
                                    />
                                )}
                            </Stack>
                        </Stack>
                    </Box>
                </Box>
            </Stack>
            {renderNavigationButtons()}
        </Box>
    );
}

// Reusable components for cleaner code
interface FullscreenExitButtonProps {
    onExitPreview: () => void;
}

function FullscreenExitButton({ onExitPreview }: Readonly<FullscreenExitButtonProps>) {
    return (
        <IconButton
            onClick={onExitPreview}
            sx={{
                ':hover': {
                    backgroundColor: 'bgBlue.main',
                },
                backgroundColor: 'primary.main',
                color: 'white',
                opacity: 0.9,
                position: 'absolute',
                right: 2.5,
                top: 2.5,
                zIndex: 1500,
            }}
        >
            <FullscreenExitIcon />
        </IconButton>
    );
}

interface SleepingScreenProps {
    showExtraText?: boolean;
    small?: boolean;
    textColor?: string;
    timezone?: string;
}

function SleepingScreen({ showExtraText = false, small, textColor, timezone }: Readonly<SleepingScreenProps>) {
    return (
        <Stack
            sx={{
                alignItems: 'center',
                color: textColor || '#ffffff',
                display: 'flex',
                gap: 3,
                height: '100%',
                justifyContent: 'center',
                textAlign: 'center',
            }}
        >
            <Typography
                sx={{
                    fontSize: small ? '24px' : '3rem',
                    fontWeight: 'bold',
                    mb: 2,
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                }}
            >
                😴
            </Typography>
            <Typography
                sx={{
                    fontSize: small ? '16px' : '2rem',
                    fontWeight: 'medium',
                    mb: 1,
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                }}
            >
                Infoscreen is sleeping
            </Typography>
            {showExtraText ? (
                <Typography
                    sx={{
                        fontSize: small ? '12px' : '1.2rem',
                        opacity: 0.8,
                        textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                    }}
                >
                    Come back during active hours
                </Typography>
            ) : null}
            {timezone ? (
                <Typography
                    sx={{
                        fontSize: small ? '10px' : '1rem',
                        mt: 1,
                        opacity: 0.6,
                        textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                    }}
                >
                    Timezone: {timezone}
                </Typography>
            ) : null}
        </Stack>
    );
}

// Helper function to create fullscreen container
function createFullscreenContainer({
    basePath,
    children,
    fullscreen,
    infoscreenLayout,
    onExitPreview,
    showBackgroundForImages,
}: {
    basePath: string;
    children: React.ReactNode;
    fullscreen: boolean;
    infoscreenLayout: Dashboard['socials'];
    onExitPreview?: () => void;
    showBackgroundForImages: boolean;
}) {
    return (
        <Box
            sx={{
                backgroundColor: infoscreenLayout?.backgroundColor ?? '#000000',
                backgroundImage:
                    infoscreenLayout?.backgroundImage && showBackgroundForImages
                        ? `url(${basePath}/${infoscreenLayout.backgroundImage}.webp)`
                        : '',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: infoscreenLayout?.backgroundSize || 'cover',
                height: fullscreen ? '100vh' : '100%',
                left: fullscreen ? 0 : 'auto',
                position: fullscreen ? 'fixed' : 'relative',
                top: fullscreen ? 0 : 'auto',
                width: fullscreen ? '100vw' : '100%',
                zIndex: fullscreen ? 1300 : 'auto',
            }}
        >
            {fullscreen && onExitPreview ? <FullscreenExitButton onExitPreview={onExitPreview} /> : null}
            {children}
        </Box>
    );
}
